/**
 * AppShell — persistent chrome for every authenticated route.
 *
 * Header holds the mark, the primary nav (Dashboard / Alerts / Config /
 * Settings), and Sign Out. Content slot is whatever the route renders.
 *
 * Active-route matching is a prefix check against `location.pathname` so
 * child pages (e.g. an `/alerts/:id` detail view down the line) still show
 * Alerts as active. `/` and `/dashboard` both light up Dashboard so the
 * masthead brand-link isn't left orphaned.
 */

import type { ComponentChildren } from 'preact';
import { useLocation } from 'preact-iso';
import { authSession, logout } from '../state/session';
import { BillingBanner } from './BillingBanner';
import { EmailVerifyBanner } from './EmailVerifyBanner';
import { FlashToast } from './FlashToast';

interface Props {
  children: ComponentChildren;
}

const NAV = [
  { href: '/dashboard', label: 'Dashboard' },
  { href: '/alerts', label: 'Alerts' },
  // Renamed from "Config" when per-device settings landed: this page is now
  // the template devices inherit, not the only config there is.
  { href: '/config', label: 'Family defaults' },
  { href: '/settings', label: 'Settings' },
] as const;

function isActive(pathname: string, target: string): boolean {
  if (target === '/dashboard') {
    return pathname === '/' || pathname.startsWith('/dashboard');
  }
  return pathname.startsWith(target);
}

export function AppShell({ children }: Props) {
  const location = useLocation();

  async function onSignOut(): Promise<void> {
    await logout();
    location.route('/login', true);
  }

  return (
    <div class="min-h-screen bg-surface">
      <header class="border-line sticky top-0 z-10 border-b bg-white/95 backdrop-blur">
        {/* Two-row header on mobile: brand + Sign out on top, nav below on
            its own line. Collapses to a single row from sm: up. Prevents
            the "ALEI" clipped-label bug that showed up at <400px viewport
            widths. */}
        <div class="mx-auto flex max-w-4xl flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:gap-6">
          <div class="flex items-center justify-between sm:contents">
            <a href="/dashboard" class="flex shrink-0 items-center gap-2">
              <img src="/logo/mark.svg" alt="" class="h-6 w-6" />
              <span class="text-[15px] font-semibold tracking-tight">
                Backstop
              </span>
            </a>
            {/* On mobile this button sits inline in the top row (right side
                of the brand). On sm: `sm:contents` unwraps the mobile
                container so this button becomes the last child of the flex
                row, sitting at the far right of the header. `order-last`
                keeps it after the nav in the sm layout. */}
            <button
              type="button"
              onClick={() => {
                void onSignOut();
              }}
              class="text-surface-muted hover:text-signal shrink-0 font-mono text-[11px] tracking-[0.14em] uppercase transition-colors sm:order-last"
            >
              Sign out
            </button>
          </div>
          <nav class="-mx-1 flex items-center gap-0.5 overflow-x-auto sm:mx-0 sm:flex-1 sm:gap-1">
            {NAV.map((item) => {
              const active = isActive(location.path, item.href);
              return (
                <a
                  key={item.href}
                  href={item.href}
                  aria-current={active ? 'page' : undefined}
                  class={
                    'font-mono text-[10px] tracking-[0.12em] uppercase px-2 py-2 transition-colors sm:text-[11px] sm:tracking-[0.14em] sm:px-3 ' +
                    (active
                      ? 'text-signal'
                      : 'text-surface-muted hover:text-signal')
                  }
                >
                  {item.label}
                </a>
              );
            })}
          </nav>
        </div>
      </header>
      {authSession.value !== null && <EmailVerifyBanner />}
      {authSession.value !== null && <BillingBanner />}
      {children}
      <FlashToast />
    </div>
  );
}
